if (!customElements.get('advanced-back-in-stock')) {
  const fetchInterceptorKey = Symbol.for('staygolden.advancedBackInStockFetchInterceptor');

  class AdvancedProductUpsell extends HTMLElement {
    constructor () {
      super();
      this.storageKey = 'bis-subscribed-variants';
      this.currentVariant = this.dataset?.currentVariant ?? null;
      this.catchURL = '/v2/back_in_stock/subscribe';
      this.subscribedVariant = null;
      this.connectFrame = null;
      this.boundFormChangeHandle = this.formChangeHandle.bind(this);
      this.boundRenderMessage = this.renderMessage.bind(this);
      this.params = {
        settings: {
          back_instock_confirmation_message: '',
          back_instock_subscribed_message: '',
          back_instock_color_scheme: '',
        },
        variants: [],
      };
      this.variantIds = [];
      if (this.currentVariant !== null) {
        this.currentVariant = this.currentVariant * 1;
      }
      const blockSettingsData = this.querySelector(`*[data-params]`)?.textContent;
      try {
        if (blockSettingsData) {
          this.params = JSON.parse(blockSettingsData);
        }
      } catch (e) {
        console.warn(e.message);
      }
    }

    get container () {
      return this.querySelector('#ps__bis_container_root');
    }

    disconnectedCallback () {
      if (this.connectFrame !== null) {
        cancelAnimationFrame(this.connectFrame);
        this.connectFrame = null;
      }

      document.removeEventListener('variant:change', this.boundFormChangeHandle);
      this.form?.removeEventListener('BISSubscribed', this.boundRenderMessage);
      this.destroyCatchRequest();
      this.form = null;
    }

    get storage () {
      let _storage;
      try {
        _storage = localStorage.getItem(this.storageKey);
        if (!_storage) return [];
        _storage = JSON.parse(_storage);
        if (!Array.isArray(_storage)) return [];
      } catch (e) {
        console.warn(e);
        _storage = [];
      }
      return _storage;
    }

    set storage (value) {
      let _storage = this.storage;
      if (!_storage.includes(value)) {
        _storage.push(value);
      }
      this.renewStorage(_storage);
    }

    renewStorage (_storage) {
      try {
        _storage = JSON.stringify(_storage);
        localStorage.setItem(this.storageKey, _storage);
      } catch (e) {
        console.warn(e);
      }
    }

    isAvailable () {
      let storage = this.storage;
      if (this.currentVariant && !this.params.variants.includes(this.currentVariant)) {
        storage = storage.filter((item) => item !== this.currentVariant);
        this.renewStorage(storage);
        return true;
      }
      return false;
    }

    formChangeHandle (e) {
      const variant = Number(e?.detail?.variant?.id) || null;
      if (variant && (!this.variantIds.length || this.variantIds.includes(variant))) {
        this.currentVariant = variant;
        this.form?.dispatchEvent(new CustomEvent('BISSubscribed'));
      }
    }

    getMessage () {
      let confirmedMessage = this.params.settings.back_instock_confirmation_message;
      let subscribedMessage = this.params.settings.back_instock_subscribed_message;
      if (!confirmedMessage) {
        confirmedMessage = subscribedMessage;
      }
      if (!subscribedMessage) {
        subscribedMessage = confirmedMessage;
      }
      return this.subscribedVariant ? confirmedMessage : subscribedMessage;
    }

    get messageContainer () {
      return this.form.querySelector('.back-in-stock-subscribed-message');
    }

    addMessage () {
      let element = this.messageContainer;
      if (!element) {
        element = document.createElement('div');
        element.classList.add('back-in-stock-subscribed-message');
        element.classList.add('gradient');
        if(this.params.settings.back_instock_color_scheme) {
          element.classList.add(`color-${this.params.settings.back_instock_color_scheme}`);
        }
        this.form.append(element);
      }
      element.innerHTML = this.getMessage();
    }

    removeMessage () {
      const element = this.messageContainer;
      if (element) {
        element.remove();
      }
    }

    renderMessage () {
      if (!this.isAvailable()) {
        if (this.storage.includes(this.currentVariant)) {
          this.addMessage();
          return;
        }
      }
      this.removeMessage();
    }

    captureSubscription (input, init) {
      const requestURL = typeof input === 'string' ? input : input?.url;
      const requestMethod = init?.method || input?.method;
      if (requestMethod?.toUpperCase() !== 'POST' || !requestURL?.includes(this.catchURL)) return;

      let submittedVariant = null;
      const requestBody = init?.body;
      try {
        if (typeof requestBody === 'string') {
          submittedVariant = JSON.parse(requestBody)?.variant_id ?? null;
        } else if (requestBody && typeof requestBody === 'object' && !(requestBody instanceof FormData)) {
          submittedVariant = requestBody.variant_id ?? null;
        } else if (requestBody instanceof FormData) {
          submittedVariant = requestBody.get('variant_id');
        }
      } catch (error) {
        console.warn('Unable to read Back in Stock request', error);
      }

      if (!submittedVariant) return;

      submittedVariant = Number(submittedVariant);
      if (!Number.isFinite(submittedVariant)) return;
      if (this.variantIds.length && !this.variantIds.includes(submittedVariant)) return;
      this.subscribedVariant = submittedVariant;
      this.storage = submittedVariant;
      this.form?.dispatchEvent(new CustomEvent('BISSubscribed'));
    }

    catchRequest () {
      let interceptor = window[fetchInterceptorKey];
      if (!interceptor) {
        interceptor = {
          originalFetch: null,
          subscribers: new Set(),
          proxy: null,
        };
        window[fetchInterceptorKey] = interceptor;
      }

      if (window.fetch !== interceptor.proxy) {
        const originalFetch = window.fetch;
        const proxy = function (input, init) {
          // If another script wrapped fetch after us and this interceptor was
          // reinstalled, an older proxy can still be present in that chain.
          // Only the latest proxy should notify subscribers.
          if (interceptor.proxy === proxy) {
            interceptor.subscribers.forEach((subscriber) => subscriber.captureSubscription(input, init));
          }
          return originalFetch.call(window, input, init);
        };
        interceptor.originalFetch = originalFetch;
        interceptor.proxy = proxy;
        window.fetch = proxy;
      }

      interceptor.subscribers.add(this);
    }

    destroyCatchRequest () {
      const interceptor = window[fetchInterceptorKey];
      if (!interceptor) return;

      interceptor.subscribers.delete(this);
      if (interceptor.subscribers.size === 0) {
        if (window.fetch === interceptor.proxy) {
          window.fetch = interceptor.originalFetch;
        }
        delete window[fetchInterceptorKey];
      }
    }

    connectedCallback () {
      if (this.connectFrame !== null || this.form) return;

      this.connectFrame = requestAnimationFrame(() => {
        this.connectFrame = null;
        if (!this.isConnected) return;

        this.form = this.querySelector('form[action="/cart/add"]');
        if (this.form) {
          const variantsData = this.querySelector('script[id^="VariantsJson-"]')?.textContent;
          if (variantsData) {
            try {
              this.variantIds = JSON.parse(variantsData).map((variant) => Number(variant.id));
            } catch (error) {
              console.warn('Unable to read product variants for Back in Stock', error);
            }
          }
          this.form.addEventListener('BISSubscribed', this.boundRenderMessage);
          this.catchRequest();
          this.form.dispatchEvent(new CustomEvent('BISSubscribed'));
          document.addEventListener('variant:change', this.boundFormChangeHandle);
        }
      });
    }
  }

  customElements.define('advanced-back-in-stock', AdvancedProductUpsell);
}
